import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../services/api';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from 'recharts';

export default function AdminReport() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/admin/surveys/${id}/report`)
      .then((res) => setData(res.data?.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <p className="text-gray-500">Chargement...</p>;
  if (!data) return <p className="text-red-600">Rapport non trouvé.</p>;

  const { survey, participation, score_global, score_distribution, radar, evolution, evolution_timeline, scores_by_service, heatmap_data, word_cloud } = data;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <Link to="/admin/surveys" className="text-primary-600 hover:underline block mb-2">← Sondages</Link>
          <h1 className="text-2xl font-bold text-gray-800">{survey?.title}</h1>
        </div>
        <div className="flex gap-2">
          <button
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
            onClick={async () => {
              try {
                const r = await api.get(`/admin/surveys/${id}/report/pdf`, { responseType: 'blob' });
                const url = URL.createObjectURL(r.data);
                const a = document.createElement('a');
                a.href = url;
                a.download = `rapport-sondage-${id}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
              } catch (e) {
                console.error(e);
              }
            }}
          >
            Export PDF
          </button>
          <button
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
            onClick={async () => {
              try {
                const r = await api.get(`/admin/surveys/${id}/report/excel`, { responseType: 'blob' });
                const url = URL.createObjectURL(r.data);
                const a = document.createElement('a');
                a.href = url;
                a.download = `rapport-sondage-${id}.xlsx`;
                a.click();
                URL.revokeObjectURL(url);
              } catch (e) {
                console.error(e);
              }
            }}
          >
            Export Excel
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Score global</p>
          <p className="text-2xl font-bold text-gray-800">{score_global ?? '—'}/10</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Participation</p>
          <p className="text-2xl font-bold text-gray-800">{participation?.total ?? 0} / {participation?.total_tokens ?? 0}</p>
          <p className="text-sm text-gray-500">{participation?.rate ?? 0}%</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Évolution vs précédent</p>
          <p className={`text-2xl font-bold ${evolution > 0 ? 'text-green-600' : evolution < 0 ? 'text-red-600' : 'text-gray-600'}`}>
            {evolution !== null ? (evolution > 0 ? '+' : '') + evolution : '—'}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Données affichables</p>
          <p className="text-lg font-medium">{participation?.can_display ? 'Oui' : 'Non'} (min {participation?.min_to_display})</p>
        </div>
      </div>

      {evolution_timeline?.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-4">Évolution des scores</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={evolution_timeline}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis domain={[0, 10]} />
                <Tooltip formatter={(v) => [v + '/10', 'Score']} />
                <Line type="monotone" dataKey="score" stroke="#2563eb" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {participation?.can_display && score_distribution?.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-4">Répartition des scores</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={score_distribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="score" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#2563eb" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {radar?.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-4">Facteurs clés (radar)</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radar}>
                <PolarGrid />
                <PolarAngleAxis dataKey="label" />
                <PolarRadiusAxis angle={90} domain={[0, 10]} />
                <Radar name="Score" dataKey="value" stroke="#2563eb" fill="#2563eb" fillOpacity={0.5} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {scores_by_service?.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-4">Scores par service</h2>
          <p className="text-sm text-gray-500 mb-2">Comparaison des scores moyens des sondages ciblés par service (période proche)</p>
          <div className="h-64 min-h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={scores_by_service} layout="vertical" margin={{ left: 80, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 10]} />
                <YAxis type="category" dataKey="service_name" width={70} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => [v + '/10', 'Score']} />
                <Bar dataKey="score" fill="#2563eb" name="Score moyen" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {heatmap_data?.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 overflow-x-auto">
          <h2 className="text-lg font-semibold mb-4">Heatmap des scores</h2>
          <p className="text-sm text-gray-500 mb-3">Répartition des scores par question (intensité = nombre de réponses)</p>
          <div className="min-w-[600px]">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr>
                  <th className="border border-gray-200 p-2 text-left bg-gray-50 font-medium">Question</th>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((s) => (
                    <th key={s} className="border border-gray-200 p-1 text-center bg-gray-50 font-medium w-10">{s}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {heatmap_data.map((row) => (
                  <tr key={row.question_id}>
                    <td className="border border-gray-200 p-2 text-gray-700 truncate max-w-[200px]" title={row.question_label}>{row.question_label}</td>
                    {row.scores.map(({ score, count, intensity }) => (
                      <td
                        key={score}
                        className="border border-gray-200 p-1 text-center"
                        style={{ backgroundColor: intensity > 0 ? `rgba(37, 99, 235, ${0.2 + intensity * 0.8})` : 'transparent' }}
                        title={`Score ${score}: ${count} réponse(s)`}
                      >
                        {count > 0 ? count : '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {word_cloud?.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-4">Nuage de mots</h2>
          <p className="text-sm text-gray-500 mb-3">Mots les plus fréquents dans les réponses libres</p>
          <div className="flex flex-wrap justify-center gap-2 py-4 px-2">
            {word_cloud.map(({ text, value }) => {
              const maxVal = Math.max(...word_cloud.map((w) => w.value));
              const size = Math.max(12, Math.min(36, 12 + (value / maxVal) * 24));
              return (
                <span
                  key={text}
                  className="inline-block px-1 hover:bg-blue-50 rounded"
                  style={{ fontSize: size, color: `hsl(220, 70%, ${50 - (value / maxVal) * 20}%)` }}
                  title={`${text}: ${value} occurrence(s)`}
                >
                  {text}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
